<template>
  <section class="ncrm-privacy-mailjet-sync" :aria-label="t('title')">
    <h3 class="text-subtitle-1 mb-1">{{ t("title") }}</h3>
    <p class="text-body-2 text-medium-emphasis mb-3">{{ t("hint") }}</p>
    <v-skeleton-loader v-if="loading && !items.length" type="list-item-two-line@2" />
    <v-alert v-else-if="error" type="error">
      {{ error }}
      <template #append><v-btn variant="text" @click="load">{{ $t("ncrm.contacts.dataProtection.record.retry") }}</v-btn></template>
    </v-alert>
    <v-alert v-else-if="!items.length" type="info">{{ t("empty") }}</v-alert>
    <template v-else>
      <v-alert v-if="notice" type="success" density="compact" class="mb-3" role="status">{{ notice }}</v-alert>
      <div v-for="item in items" :key="item.contact_point_id" class="ncrm-privacy-mailjet-sync__row">
        <span class="ncrm-privacy-mailjet-sync__email">{{ item.value }}</span>
        <v-chip :color="style(item).color">
          <v-icon start size="14">{{ style(item).icon }}</v-icon>{{ t(`status.${key(item)}`) }}
        </v-chip>
        <span v-if="item.updated_at" class="text-caption text-medium-emphasis">{{ formatSyncDate(item.updated_at) }}</span>
        <v-btn v-if="canManage && key(item) === 'failed'" size="small" variant="text" color="primary"
          :loading="retrying === item.job_id" :disabled="Boolean(retrying)" @click="retry(item)">
          {{ t("retry") }}
        </v-btn>
        <router-link v-if="canManage && key(item) === 'unmapped'" :to="mappingRoute" class="text-body-2">
          {{ t("manageLists") }}
        </router-link>
        <span v-else-if="key(item) === 'unmapped'" class="text-caption text-medium-emphasis">{{ t("managedByManager") }}</span>
        <!-- S8.5c: plângerea spam nu e opoziție automată (GDPR-035); acțiunea existentă o consemnează explicit. -->
        <template v-if="item.spam_to_review">
          <v-chip color="warning"><v-icon start size="14">mdi-alert-octagon-outline</v-icon>{{ t("spamToReview") }}</v-chip>
          <v-btn size="small" variant="text" color="primary" @click="$emit('record-objection', $event)">{{ t("recordObjection") }}</v-btn>
        </template>
      </div>
      <v-alert v-if="retryError" type="error" density="compact" class="mt-2">{{ retryError }}</v-alert>
    </template>
  </section>
</template>

<script>
import axios from "axios";
import { hasPrivacyManagerRole } from "@/Utile/ncrmPrivacyAccess.mjs";
import {
  MAILJET_LIST_MAPPING_ROUTE, SYNC_STATUS_STYLE, formatSyncDate, nextPollDelay, syncStatusKey,
} from "@/Utile/ncrmPrivacyMailjetSync.mjs";

// GDPR S8.5b — ultimul job de sincronizare Mailjet al fiecărui email al recordului; managerul Privacy reia un job
// eșuat. După reîncercare lista se reîncarcă și apare o confirmare discretă; o eroare rămâne inline. Workerul închide
// jobul la un tick ulterior (sau mai târziu, într-un backlog mare), deci lista se recitește după scadența jobului
// `pending` (`nextPollDelay`), fără limită de număr, cu un ritm care scade până la `maxMs` cât timp jobul tot așteaptă.
// Se oprește numai la o stare finală, la un job evaluat în `dry_run` sau la demontare.
const POLL = Object.freeze({ everyMs: 15000, maxMs: 5 * 60000 });

export default {
  name: "NcrmPrivacyMailjetSyncStatus",
  emits: ["record-objection"],
  props: {
    subjectType: { type: String, required: true },
    subjectId: { type: [String, Number], required: true },
  },
  data: () => ({ items: [], loading: false, error: "", retrying: null, retryError: "", notice: "", canManage: false,
    requestSequence: 0, polls: 0, pollTimer: null, disposed: false }),
  computed: { mappingRoute: () => MAILJET_LIST_MAPPING_ROUTE },
  // Alt record în aceeași componentă: datele vechi dispar, iar un răspuns întârziat al recordului vechi se ignoră.
  watch: { subjectId() { Object.assign(this, { items: [], notice: "", retryError: "", retrying: null, polls: 0 }); this.load(); } },
  mounted() { this.canManage = hasPrivacyManagerRole(); this.load(); },
  // Închis: nicio citire nouă, nici din continuarea unei reîncercări încă în zbor.
  beforeUnmount() { this.disposed = true; clearTimeout(this.pollTimer); this.requestSequence += 1; },
  methods: {
    t(key, params) { return this.$t(`ncrm.contacts.dataProtection.record.mailjetSync.${key}`, params); },
    key: syncStatusKey,
    style(item) { return SYNC_STATUS_STYLE[syncStatusKey(item)]; },
    formatSyncDate,
    async load() {
      clearTimeout(this.pollTimer);
      if (this.disposed) return;
      const sequence = ++this.requestSequence;
      this.loading = true; this.error = "";
      try {
        const { data } = await axios.get("/ncrm/privacy/mailjet-sync/status",
          { params: { subject_type: this.subjectType, subject_id: this.subjectId } });
        if (sequence !== this.requestSequence) return;
        this.items = Array.isArray(data?.items) ? data.items : [];
        const delay = nextPollDelay(this.items, { everyMs: POLL.everyMs, slowMs: POLL.maxMs });
        if (delay !== null) {
          const slower = Math.min(POLL.everyMs * 2 ** Math.floor(this.polls / 4), POLL.maxMs);
          this.polls += 1;
          this.pollTimer = setTimeout(() => this.load(), Math.max(delay, slower));
        }
      } catch {
        if (sequence === this.requestSequence) this.error = this.t("loadError");
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    // Răspunsul unei reîncercări contează numai pentru recordul pe care a fost cerută și cât timp secțiunea e montată.
    async retry(item) {
      const subject = `${this.subjectType}:${this.subjectId}`;
      const current = () => !this.disposed && subject === `${this.subjectType}:${this.subjectId}`;
      this.retrying = item.job_id; this.retryError = ""; this.notice = "";
      try {
        await axios.post(`/ncrm/privacy/mailjet-sync/jobs/${item.job_id}/retry`);
        if (!current()) return;
        this.notice = this.t("retried");
        this.polls = 0; // un job nou în așteptare: urmărirea reîncepe
        await this.load();
      } catch {
        if (current()) this.retryError = this.t("retryError");
      } finally {
        if (current()) this.retrying = null;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-mailjet-sync__row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 8px 0; }
.ncrm-privacy-mailjet-sync__email { font-weight: 600; overflow-wrap: anywhere; min-width: 0; }
</style>
